import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MESSAGE_TEXT, DialogType, BUTTON, TITLE_SETTINGS } from '@core/constants';
import { DpadZoneType } from '@core/constants';


/**
 * Everything this dialog reads out of DynamicDialogConfig.data.
 *
 * PrimeNG types `data` as `any`, so a caller only gets these keys checked once it
 * declares its own object as ConfirmDialogData - see the callers in bath-info and
 * bath-dai-setting. Panel size is read from here rather than from the top-level
 * `width`/`height` of DynamicDialogConfig because the constructor sets `config.style`
 * unconditionally; a caller that needs a non-default size has to say so through these.
 */
export interface ConfirmDialogData {
  /** Body text. Rendered through innerHTML, so it may carry markup. */
  message?: string;
  /** true shows the yes/no pair, false shows a lone close button. */
  isOkCancel?: boolean;
  /** Selects the icon and the fallback message when `message` is omitted. */
  dialogType?: DialogType;
  /** Passed through to the buttons for their alternate styling. */
  cssCustom?: boolean;
  /** Let the panel grow to its message instead of holding the fixed default height. */
  fitContent?: boolean;
  /** Panel width. Defaults to 140px, which suits a short one-line message. */
  width?: string;
  /** Panel height. Ignored when `fitContent` is set. Defaults to 150px. */
  height?: string;
}

@Component({
  selector: 'caremoni-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConfirmDialogComponent implements OnInit{
  
  TITLE_SETTINGS = TITLE_SETTINGS
  BUTTON = BUTTON
  DpadZoneType = DpadZoneType;
  dialogType: DialogType = DialogType.save;
  urlImage: string = '../../../../../assets/images/save.svg';
  message: string = '';
  isOkCancel: boolean = false;
  cssCustom: boolean = false;
  /** Let the panel grow to its message instead of holding the fixed default size. */
  fitContent: boolean = false;

  /** One typed view over the untyped config.data, so every read below is checked. */
  private get data(): ConfirmDialogData {
    return this.config?.data ?? {};
  }

  constructor(public ref: DynamicDialogRef,
    public config: DynamicDialogConfig) {
      this.cssCustom = false;
      this.config.showHeader = false;
      // Sized for a short one-line message. A caller with a longer message can ask for
      // more room through data.width, and for data.fitContent to have the panel follow
      // its own height; everything else keeps these.
      this.fitContent = this.data.fitContent ?? false;
      this.config.style = {
        width: this.data.width ?? '140px',
        height: this.fitContent ? 'auto' : (this.data.height ?? '150px')
      };
      this.config.contentStyle = {'border-radius': '12px'};
      // Marks the panel PrimeNG puts around this component, so the stylesheet can round
      // and clip it to the same 12px as the box inside without touching other dialogs.
      this.config.styleClass = 'confirm-dialog-panel';
  }

  ngOnInit(): void {
    this.cssCustom = this.data.cssCustom ?? false;
    this.isOkCancel = this.data.isOkCancel ?? false;
    // set message from dialog config
    if(this.data.message != undefined) {
      this.message = this.data.message;
    }
    // set dialog type from dialog config
    if(this.data.dialogType != undefined){
      this.dialogType = this.data.dialogType;
    }

    //削除処理後に表示される
    if (this.dialogType === DialogType.delete) {
      this.urlImage = '../../../../../assets/images/delete.svg';
      this.message = this.message || MESSAGE_TEXT.DELETE_COMPLETE;
    }
    //新規、編集、削除処理時に失敗した場合に表示される
    else if (this.dialogType === DialogType.error) {
      this.urlImage = '../../../../../assets/images/error.svg';
      this.message = this.message || MESSAGE_TEXT.ERR;
    }
    //新規、編集処理後に成功した場合に表示される
    else if (this.dialogType === DialogType.save) {
      this.urlImage = '../../../../../assets/images/save.svg';
      this.message = this.message || MESSAGE_TEXT.SAVE_COMPLETE;
    }
    //編集処理後に表示される
    else {
      this.urlImage = '../../../../../assets/images/edit.svg';
      this.message = this.message || MESSAGE_TEXT.UPDATE_COMPLETE;
    }
    //setTimeout(() => this.ref.close('reject'), 1200);
  }

  onSubmit(){
    this.ref.close('ok');
  }

  closeModal(){
    this.ref.close('cancel');
  }
}
